/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "DNS & máy chủ tên" (v2.1): the delegation — our name servers (with their IPv4/IPv6 glue)
 * beside what the registry answered at the last reconciliation, host by host and address by
 * address where the registry's domain view carries them (`snapshot.host_addrs`), and the
 * discrepancy reconciliation opened when they differ. Below, the kind of name servers the domain
 * uses and the zone the system keeps for it, if any, with its records (`DnsZone.tsx`).
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { useDomainPage } from '../context';
import { useVocab } from '../refs';
import { DnsZonePart } from './DnsZone';
import { EditAction, type DomainTabProps } from './shared';
import { Facts } from './SubjectContactsTab';

interface NsRow {
  host: string;
  ours: boolean;
  addresses: string[];
  atRegistry: boolean | null;
  /** The addresses the registry reported for the host; null when they are not compared (see `host_addrs`). */
  registryAddresses: string[] | null;
}

function rows(intended: { host: string; addresses: string[] }[], observed: string[] | null, glue: { host: string; addresses: string[] }[] | null | undefined): NsRow[] {
  const seen = new Set(observed?.map((h) => h.toLowerCase()) ?? []);
  const at = (h: string) => (glue ? (glue.find((g) => g.host.toLowerCase() === h.toLowerCase())?.addresses ?? []) : null);
  const out: NsRow[] = intended.map((n) => ({ host: n.host, ours: true, addresses: n.addresses, atRegistry: observed ? seen.has(n.host.toLowerCase()) : null, registryAddresses: at(n.host) }));
  for (const h of observed ?? []) if (!intended.some((n) => n.host.toLowerCase() === h.toLowerCase())) out.push({ host: h, ours: false, addresses: [], atRegistry: true, registryAddresses: at(h) });
  return out;
}

const sameAddresses = (a: string[], b: string[]) => a.map((x) => x.toLowerCase()).sort().join() === b.map((x) => x.toLowerCase()).sort().join();

/** A name server's addresses: one line for IPv4, one for IPv6. */
function Ips({ list }: { list: string[] }) {
  const { t } = useTranslation('domainPage');
  const v4 = list.filter((a) => !a.includes(':'));
  const v6 = list.filter((a) => a.includes(':'));
  return (
    <>
      {v4.length ? <span className="font-mono text-xs text-muted-foreground">{t('dns.ipv4', { list: v4.join(', ') })}</span> : null}
      {v6.length ? <span className="font-mono text-xs text-muted-foreground">{t('dns.ipv6', { list: v6.join(', ') })}</span> : null}
    </>
  );
}

export function DnsTab({ detail }: DomainTabProps) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const vocab = useVocab();
  const { delegation, delegationFailed, registryState } = useDomainPage();
  const snap = registryState?.snapshot ?? null;
  const issue = registryState?.issues?.find((i) => i.issue_type === 'NAMESERVER' && (i.state === 'OPEN' || i.state === 'AUTO_RECONCILING' || i.state === 'MANUAL_REVIEW'));
  if (!delegation && delegationFailed) {
    return (
      <Section title={t('dns.title')} description={t('dns.hint')} actions={<EditAction detail={detail} action="nameservers" label={t('dns.change')} />}>
        <DataError error={delegationFailed.error} onRetry={delegationFailed.retry} title={td('delegation.loadFailed')} />
      </Section>
    );
  }
  if (!delegation) return <Loading rows={3} />;
  const list = rows(delegation.nameservers, snap?.nameservers ?? null, snap?.host_addrs);
  // Every name server matches by name: the issue is about their IP addresses (glue) only.
  const addressesOnly = snap !== null && list.every((r) => r.ours && r.atRegistry === true);
  return (
    <>
      <Section
        title={t('dns.title')}
        description={t('dns.hint')}
        actions={<EditAction detail={detail} action="nameservers" label={t('dns.change')} />}
        flush
      >
        {issue ? (
          <div className="px-5 pb-4">
            <Callout tone="warn" title={t(addressesOnly ? 'dns.driftAddressesTitle' : 'dns.driftTitle')}>
              {t('dns.driftBody', { at: formatDateTime(issue.detected_at) })}
            </Callout>
          </div>
        ) : null}
        <DataTable<NsRow>
          columns={[
            { key: 'n', header: '#', cell: (r) => <span className="text-muted-foreground tabular">{list.indexOf(r) + 1}</span> },
            {
              key: 'ours',
              header: t('dns.ours'),
              className: 'min-w-44 whitespace-normal',
              cell: (r) =>
                r.ours ? (
                  <span className="grid">
                    <span className="font-mono text-[13px]">{r.host}</span>
                    <Ips list={r.addresses} />
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                ),
            },
            {
              key: 'registry',
              header: t('dns.atRegistry'),
              className: 'min-w-44 whitespace-normal',
              cell: (r) =>
                r.atRegistry === null ? (
                  <span className="text-muted-foreground">{t('dns.notRead')}</span>
                ) : r.atRegistry ? (
                  <span className="grid">
                    <span className="font-mono text-[13px]">{r.host}</span>
                    <Ips list={r.registryAddresses ?? []} />
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                ),
            },
            {
              key: 'match',
              header: t('dns.match'),
              cell: (r) =>
                r.atRegistry === null ? (
                  <Badge tone="neutral" icon={false}>{t('dns.unknown')}</Badge>
                ) : r.ours && r.atRegistry ? (
                  r.registryAddresses !== null && !sameAddresses(r.addresses, r.registryAddresses) ? (
                    <Badge tone="warn">{t('dns.addressesDiffer')}</Badge>
                  ) : (
                    <Badge tone="ok">{t('dns.same')}</Badge>
                  )
                ) : (
                  <Badge tone="warn">{r.ours ? t('dns.missingAtRegistry') : t('dns.onlyAtRegistry')}</Badge>
                ),
            },
          ]}
          rows={list}
          rowKey={(r) => `${r.ours ? 'o' : 'r'}-${r.host}`}
        />
      </Section>
      <Section title={t('dns.delegation')}>
        <Facts
          rows={[
            [t('dns.state'), vocab('dnsState', delegation.dns_state)],
            [t('dns.hostObjects'), delegation.supports_host_objects === null ? null : delegation.supports_host_objects ? t('dns.yes') : t('dns.no')],
            [t('dns.snapshot'), snap ? formatDateTime(snap.captured_at) : t('dns.notRead')],
            [t('dns.registry'), delegation.registry_name],
          ]}
        />
      </Section>
      <DnsZonePart detail={detail} hosts={delegation.nameservers.map((n) => n.host)} />
    </>
  );
}
